import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { documentListApiService } from 'src/services';
import { useDashboardStore } from 'src/store/dashboard.store';

const PRELOAD_TTL_MS = 60_000;

export const useDashboardPreloadStore = defineStore('dashboard_preload', {
  state: () => ({
    response: null as dynamicObj | null,
    queryParams: '' as string,
    preloadedAt: 0 as number,
  }),

  getters: {
    isPreloadFresh: (state): boolean => {
      if (!state.response || !state.preloadedAt) return false;
      return Date.now() - state.preloadedAt < PRELOAD_TTL_MS;
    },
  },

  actions: {
    /**
     * Called from PrepareDashboard Step 5 — fetches dashboard list data using
     * the user's saved filters/pagination so dashboard.vue can consume it on mount.
     */
    async prefetchDashboardData(): Promise<void> {
      try {
        const dashboardStore = useDashboardStore();

        // Reuse existing store methods to build the same query params dashboard.vue would use
        await dashboardStore.getPaginationSize();
        await dashboardStore.getOrderBy();
        dashboardStore.getSavedFilters();
        await dashboardStore.getSelectedDashboardFromUserPreferences();
        await dashboardStore.getSavedColumns();

        const pageSize = Number(dashboardStore.pagination_size) || 20;
        const filterParams = await dashboardStore.makeQueryParamFromFilters(false);
        const queryParams = `?&skip=0&limit=${pageSize}${filterParams}`;

        const response = await documentListApiService.getList(queryParams);
        this.response = response;
        this.queryParams = queryParams;
        this.preloadedAt = Date.now();
      } catch (err) {
        // Silent fail — dashboard.vue will fetch normally on mount
        this.clear();
      }
    },

    /**
     * Consume the preloaded response. Returns the response and clears the cache.
     */
    consume(): dynamicObj | null {
      const result = this.response;
      this.clear();
      return result;
    },

    clear(): void {
      this.response = null;
      this.queryParams = '';
      this.preloadedAt = 0;
    },
  },
});
